/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	ButtonList,
	CosmosAsset,
	CosmosCard,
	EdsIcon,
	TitleDescStack,
	TitleDescStackProps,
} from "@components/elements";
import { AriesCardFragment } from "@cosmos/hygraph-schema";
import { css, cva, cx } from "@panda/css";
import { Flex, Stack } from "@panda/jsx";
import { CSSProperties } from "react";

const borderRadiusPx = 24;

const cardStyles = css({
	position: "relative",
	overflow: "hidden",
	minHeight: "400px",
	borderRadius: `eds.borderRadius.${borderRadiusPx}`,
	padding: "eds.dimension.64",
	border: "1px solid token(colors.eds.palette.transparent.light.010)",
	smDown: {
		paddingBlock: "eds.dimension.32",
		minHeight: "350px",
	},
	"&:has(:focus-visible), &:is(:focus-visible,:hover)": {
		"& .aries-background-image": {
			"& img, & video": {
				transform: "scale(1.05)",
			},
		},
	},
	"& .aries-background-image": {
		"& img, & video": {
			transition: "transform token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
		},
	},
	"& .cosmos-button:first-child": {
		_before: {
			content: "''",
			display: "flex",
			position: "absolute",
			inset: "-1500px",
		},
	},
});

const widthStyles = cva({
	base: {
		flexBasis: "calc(50% - 8px)",
		mdDown: {
			flexBasis: "100%",
		},
	},
	variants: {
		fullWidth: {
			true: {
				flexBasis: "100%",
			},
			false: {},
		},
	},
});

const backgroundGradientStyles = css({
	position: "absolute",
	top: 0,
	w: "full",
	h: "full",
	background: "linear-gradient(90deg, #000 0%, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.00) 100%)",
});

export interface AriesCardProps extends AriesCardFragment {
	className?: string;
}

const style = { "--asset-border-radius": `${borderRadiusPx}px` } as CSSProperties;

export function AriesCard({
	icon,
	title,
	desc,
	buttons,
	asset,
	fullWidth = false,
	className = "",
}: AriesCardProps): React.JSX.Element {
	const titleDescStack: TitleDescStackProps = {
		title: {
			html: title?.html,
			text: title?.text,
			variant: "headingLG",
			as: "h3",
		},
		description: {
			html: desc,
			variant: "paragraphMD",
			color: "eds.text.primary",
		},
		stack: { gap: "eds.dimension.8" },
	};

	return (
		<CosmosCard
			className={cx("aries-card", className, cardStyles, widthStyles({ fullWidth: !!fullWidth }))}
			style={style}
			variant="outlinedThin"
		>
			<Flex
				alignItems="stretch"
				justifyContent="center"
				objectFit="contain"
				minWidth="0"
				inset="0"
				className={cx("aries-background-image")}
				position="absolute"
			>
				<CosmosAsset
					asset={asset}
					className={css({
						objectFit: "cover",
						h: "full",
						w: "full",
						position: "relative",
					})}
				/>
				<div className={backgroundGradientStyles} />
			</Flex>
			<Stack
				className={cx("aries-card-content")}
				direction="column"
				gap="eds.dimension.32"
				maxWidth="240px"
				transform="translateZ(0)"
			>
				{icon ? <EdsIcon icon={icon} size="xl" /> : null}
				<TitleDescStack {...titleDescStack}></TitleDescStack>
				{buttons ? <ButtonList size="lg" buttons={buttons} /> : null}
			</Stack>
		</CosmosCard>
	);
}
